/**
 * Spec polarity — the single definition of how `Spec.higher_is_better` colours
 * a two-threshold bar.
 *
 * The scorer requires `lower_threshold < upper_threshold` on the number line in
 * BOTH directions (`ScorecardItem._score_numeric`, `UnifiedScoringService`), so
 * a bar always renders lower_threshold physically LEFT of upper_threshold.
 * Polarity flips the zone *meanings*, never the positions:
 *
 *   higherIsBetter=true:   [fail] lower [acceptable] upper [desirable]
 *   higherIsBetter=false:  [desirable] lower [acceptable] upper [fail]
 *
 * Never swap the stored thresholds to make a bar read correctly — that silently
 * breaks scoring (DEV-792).
 */

export type ZoneRole = 'fail' | 'acceptable' | 'desirable'

export interface PolarityZones {
  /** Band left of lower_threshold. */
  left: ZoneRole
  /** Band between the thresholds — always the acceptable middle. */
  middle: ZoneRole
  /** Band right of upper_threshold. */
  right: ZoneRole
}

const HIGHER_IS_BETTER: PolarityZones = { left: 'fail', middle: 'acceptable', right: 'desirable' }
const LOWER_IS_BETTER: PolarityZones = { left: 'desirable', middle: 'acceptable', right: 'fail' }

/** Which role each band of a threshold bar carries, given the spec's polarity. */
export const polarityZones = (higherIsBetter: boolean): PolarityZones =>
  higherIsBetter ? HIGHER_IS_BETTER : LOWER_IS_BETTER

/** Human label for the current polarity, for tooltips and aria text. */
export const polarityLabel = (higherIsBetter: boolean): string =>
  higherIsBetter ? 'Higher is better' : 'Lower is better'
